const TOKEN_KEY = 'flock_sales_portal_token';

async function request(path, options = {}) {
  const token = localStorage.getItem(TOKEN_KEY);
  const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';
  const response = await fetch(`${baseUrl}${path}`, {
    ...options,
    headers: {
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(body.message || body.error || 'Sales portal request failed');
    error.status = response.status;
    error.body = body;
    throw error;
  }
  return body.data ?? body;
}

function json(method, payload) {
  return {
    method,
    body: JSON.stringify(payload || {}),
  };
}

function query(params) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, value);
    }
  });
  const text = search.toString();
  return text ? `?${text}` : '';
}

// Streaming POST for the Ask Flock SSE endpoint. The shared request() helper
// parses JSON, so streaming gets its own reader: yields `data:` frames to
// onDelta ({delta} payloads) and named frames (done/error) to onEvent.
// Non-2xx or non-SSE responses (limits, kill-switch, validation) throw the
// same shaped error as request().
export async function streamRequest(path, payload, { onDelta, onEvent, signal } = {}) {
  const token = localStorage.getItem(TOKEN_KEY);
  const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';
  const response = await fetch(`${baseUrl}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload || {}),
    signal,
  });

  const contentType = response.headers.get('content-type') || '';
  if (!response.ok || !contentType.includes('text/event-stream')) {
    const body = await response.json().catch(() => ({}));
    const error = new Error(body.message || body.error || 'Sales portal request failed');
    error.status = response.status;
    error.body = body;
    throw error;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let frameEnd;
    while ((frameEnd = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, frameEnd);
      buffer = buffer.slice(frameEnd + 2);

      let eventName = 'message';
      const dataLines = [];
      for (const line of frame.split('\n')) {
        if (line.startsWith(':')) continue; // heartbeat comment
        if (line.startsWith('event:')) {
          eventName = line.slice(6).trim();
        } else if (line.startsWith('data:')) {
          dataLines.push(line.slice(5).trimStart());
        }
      }
      if (!dataLines.length) continue;

      let data;
      try {
        data = JSON.parse(dataLines.join('\n'));
      } catch {
        continue;
      }

      if (eventName === 'message' && data.delta !== undefined) {
        onDelta?.(data.delta);
      } else {
        onEvent?.(eventName, data);
      }
    }
  }
}

export function saveToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export const salesApi = {
  login: (payload) => request('/sales/auth/login', json('POST', payload)),
  me: () => request('/sales/me'),
  summary: () => request('/sales/summary'),
  leads: (params = {}) => request(`/sales/leads${query(params)}`),
  leadWorkbench: () => request('/sales/leads/workbench'),
  availableLeads: (params = {}) => request(`/sales/leads/available${query(params)}`),
  lead: (leadId) => request(`/sales/leads/${leadId}`),
  claimLead: (leadId) => request(`/sales/leads/${leadId}/claim`, json('POST')),
  releaseLead: (leadId) => request(`/sales/leads/${leadId}/release`, json('POST')),
  updateLeadStatus: (leadId, status) => request(`/sales/leads/${leadId}/status`, json('PATCH', { status })),
  createLeadNote: (leadId, body) => request(`/sales/leads/${leadId}/notes`, json('POST', { body })),
  templates: () => request('/sales/templates'),
  collateral: () => request('/sales/collateral'),
  commissions: () => request('/sales/commissions'),
  commissionDeal: (organizationId, params = {}) => request(`/sales/commissions/deal/${encodeURIComponent(organizationId)}${query(params)}`),
  payouts: () => request('/sales/payouts'),
  notifications: () => request('/sales/notifications'),
  readNotification: (notificationId) => request(`/sales/notifications/${notificationId}/read`, json('PATCH')),
  impact: () => request('/sales/impact'),
  reps: () => request('/sales/admin/reps'),
  repCommissions: () => request('/sales/admin/rep-commissions'),
  conflicts: () => request('/sales/admin/conflicts'),
  commissionRules: () => request('/sales/admin/commission-rules'),
  createCommissionRule: (payload) => request('/sales/admin/commission-rules', json('POST', payload)),
  updateCommissionRule: (ruleId, payload) => request(`/sales/admin/commission-rules/${encodeURIComponent(ruleId)}`, json('PATCH', payload)),
  archiveCommissionRule: (ruleId) => request(`/sales/admin/commission-rules/${encodeURIComponent(ruleId)}`, json('DELETE')),
  seedChurchDemand: (payload) => request('/sales/admin/seed-church-demand', json('POST', payload)),
  createManualLead: (payload) => request('/sales/leads', json('POST', payload)),
  assignLead: (leadId, assignedTo) => request(`/sales/admin/leads/${leadId}/assign`, json('POST', { assignedTo })),
  releaseBatch: (payload) => request('/sales/admin/batches/release', json('POST', payload)),
  approvePayout: (payoutId, markPaid = false) => request(`/sales/admin/payouts/${payoutId}/approve`, json('POST', { markPaid })),
  createPayout: (payload) => request('/sales/admin/payouts', json('POST', payload)),
  payoutStatement: (payoutId) => request(`/sales/payouts/${encodeURIComponent(payoutId)}/statement`),
  pendingStatement: (params = {}) => request(`/sales/payouts/pending-statement${query(params)}`),
  resolveConflict: (conflictId, payload) => request(`/sales/admin/conflicts/${conflictId}/resolve`, json('POST', payload)),
  chatConversations: () => request('/sales/chat/conversations'),
  searchChatConversations: (q, team = false) =>
    request(`/sales/chat/conversations/search${query({ q, scope: team ? 'team' : undefined })}`),
  createChatConversation: (payload) => request('/sales/chat/conversations', json('POST', payload)),
  chatMessages: (conversationId) => request(`/sales/chat/conversations/${encodeURIComponent(conversationId)}/messages`),
  chatAdminOverview: () => request('/sales/chat/admin/overview'),
  sendChatMessage: (conversationId, content, handlers) =>
    streamRequest(`/sales/chat/conversations/${encodeURIComponent(conversationId)}/messages`, { content }, handlers),
};

export function referralUrl(slug) {
  // The referral landing page is served by the backend at the ROOT path /r/:slug
  // (not under /api). VITE_API_BASE_URL includes /api (house convention), so strip
  // it to get the bare backend origin for the shareable link.
  const apiBase = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api').replace(/\/+$/, '');
  const origin = apiBase.replace(/\/api$/, '');
  return `${origin}/r/${slug}`;
}
